import type { WishlistItemFragment } from '#graphql-operations'
import type { ComputedRef, Ref } from 'vue'
import { guestOnlyVariantIds } from '~/utils/guestWishlist'

/**
 * The full list a wishlist mutation replies with — or an error. A failed mutation still resolves
 * (`useGraphqlMutation` does not throw on GraphQL errors), and as these fields are non-null its
 * `data` comes back null: read as a reply, that would empty the whole list on screen while the
 * server still holds it. So the list shown is kept and the caller's error path runs instead — the
 * heart's rollback, the Undo's catch. A reply that did arrive is used even beside an error on some
 * nested field: the change itself was made.
 */
function wishlistReply<T>(list: readonly T[] | null | undefined, errors: readonly { message?: string }[] | undefined, operation: string): T[] {
  if (!list)
    throw new Error(errors?.[0]?.message ?? `${operation} returned no list`)
  return [...list]
}

/** The store keeps outfits as ids; every read and reply is reduced to them here. */
function toLookIds(looks: { id: string | number }[] | null | undefined): string[] {
  return (looks ?? []).map(look => String(look.id))
}

/**
 * The wishlist, for signed-in customers and guests alike — products AND
 * outfits, one list on one page.
 *
 * A guest's saves live in the browser (see `useGuestWishlist` and
 * `useGuestSavedLooks`) and are merged into the account the first time
 * `loggedIn` turns true — which covers signing in, completing registration,
 * and a session silently restored from a cookie, because every one of those
 * paths reaches this plugin only through that flag.
 *
 * Outfits are held as ids only: a heart needs nothing more, the header badge
 * needs nothing more, and the wishlist page fetches the outfits behind the ids
 * when it shows them (`useSavedLookRows`). Their own tables and mutations on
 * the backend, the same life here.
 *
 * Nothing here reads storage during setup and the server always resolves to an
 * empty list, so a CDN-cached document never carries anyone's wishlist.
 */
export default defineNuxtPlugin({
  name: 'observed',
  async setup(_nuxtApp) {
    const state = useState<WishlistItemFragment[]>('wishlist', () => [])
    const loggedIn = computed(() => useAuth()?.loggedIn.value)
    const customerId = computed(() => useAuth()?.activeCustomer.value?.id ?? null)
    const guest = useGuestWishlist()

    // Counted, not a flag: with two things in flight, the first to finish would
    // otherwise clear it while the other still runs (LookHeader's heart keys off it).
    const inFlight = ref(0)
    const loading = computed(() => inFlight.value > 0)

    /**
     * Every account read and write, one at a time, each applying its own reply
     * before the next is sent. Each reply is the whole list as the server held it
     * at that moment, so two in flight together — two hearts, a heart and the
     * refresh, a heart and the sign-in merge — could land in either order, and
     * the older one would put back a card just removed (or drop one just saved).
     * A reply is dropped when the session changed on its way: a sign-out must not
     * have the previous customer's list written back over it.
     *
     * Only single requests go through here, never a whole refresh or merge that
     * itself queues requests — that would wait on itself.
     *
     * Each step is bounded (`WISHLIST_STEP_TIMEOUT`): no fetch on the way has a
     * timeout of its own, and one stalled request would otherwise hold every heart
     * on the site behind it. A step that times out rejects (the heart rolls back,
     * the next read corrects it) and its reply, should it still come, is dropped.
     */
    const WISHLIST_STEP_TIMEOUT = 10_000
    let queue: Promise<unknown> = Promise.resolve()
    function serially<T>(request: () => Promise<T>, apply: (reply: T) => void): Promise<void> {
      inFlight.value++
      const run = queue
        .then(async () => {
          const session = customerId.value
          let timer: ReturnType<typeof setTimeout> | undefined
          const timeout = new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error('The wishlist request timed out')), WISHLIST_STEP_TIMEOUT)
          })
          try {
            const reply = await Promise.race([request(), timeout])
            if (customerId.value === session)
              apply(reply)
          }
          finally {
            clearTimeout(timer)
          }
        })
        .finally(() => {
          inFlight.value--
        })
      queue = run.catch(() => {})
      return run
    }

    /** The account's saved outfits, newest first — ids, see the note above. */
    const savedLookIds = useState<string[]>('saved-look-ids', () => [])
    const guestLooks = useGuestSavedLooks()

    // One merge at a time. Two flips can land together — `authLogin` refreshes
    // the customer while the deferred cookie restore is already in flight — and
    // without this they would both send the same adds.
    let mergeInFlight: Promise<void> | null = null
    let lookMergeInFlight: Promise<void> | null = null

    /** The outfit twin of `mergeGuestWishlist`, one id at a time like it. */
    async function mergeGuestSavedLooks(): Promise<void> {
      if (!import.meta.client || !guestLooks.entries.value.length)
        return

      if (lookMergeInFlight)
        return lookMergeInFlight

      const onAccount = new Set(savedLookIds.value)
      const missing = guestLooks.entries.value.filter(id => !onAccount.has(id))

      if (!missing.length) {
        guestLooks.clear()
        return
      }

      lookMergeInFlight = (async () => {
        for (const lookId of missing) {
          await serially(
            // Un-saved since the merge began (its removal already ran), or saved
            // onto the account meanwhile: sending it would bring it back or repeat it.
            async () => guestLooks.has(lookId) && !savedLookIds.value.includes(lookId)
              ? useGraphqlMutation('addLookToWishlist', { lookId })
              : null,
            // A refused add (a look disabled since) has no list: keep what is shown.
            (reply) => {
              if (reply?.data?.addLookToWishlist)
                savedLookIds.value = toLookIds(reply.data.addLookToWishlist)
            },
          )
        }

        // Cleared only once every add has settled, as for products.
        guestLooks.clear()
      })()

      try {
        await lookMergeInFlight
      }
      finally {
        lookMergeInFlight = null
      }
    }

    async function mergeGuestWishlist(): Promise<void> {
      if (!import.meta.client || !guest.entries.value.length)
        return

      if (mergeInFlight)
        return mergeInFlight

      const missing = guestOnlyVariantIds(
        guest.entries.value,
        state.value.map(item => item.productVariant.id),
      )

      // Already on the account — drop the local copy rather than re-sending it.
      if (!missing.length) {
        guest.clear()
        return
      }

      mergeInFlight = (async () => {
        for (const productVariantId of missing) {
          await serially(
            // Un-hearted since the merge began: its removal already ran, so
            // sending the add now would put back what the customer just took off.
            async () => guest.has(productVariantId) && !state.value.some(item => String(item.productVariant.id) === productVariantId)
              ? useGraphqlMutation('addToWishlist', { productVariantId })
              : null,
            (reply) => {
              if (reply?.data?.addToWishlist)
                state.value = [...reply.data.addToWishlist] as WishlistItemFragment[]
            },
          )
        }

        // Cleared only once every add has settled, so a merge that fails part
        // way is retried on the next sign-in instead of losing the list.
        guest.clear()
      })()

      try {
        await mergeInFlight
      }
      finally {
        mergeInFlight = null
      }
    }

    // The merge has two possible starting guns and needs both. Usually the local
    // list is read at `app:mounted` and the session resolves a second later, so
    // the `loggedIn` flip below carries it. But the auth route middleware can
    // resolve a session before mount, and then the flip has already happened by
    // the time there is anything to merge — without this the saves would sit in
    // the browser untouched for the rest of the visit.
    //
    // The client guard is load-bearing, not cosmetic. `guest.loaded` is flipped
    // only by `guest.load()` in `guestWishlist.client.ts`, so this watcher can
    // never fire during SSR anyway. Registering it on the server is worse than
    // useless: `watch` is the only thing here that builds a `ReactiveEffect`
    // (`computed` does not), and Vue's ReactiveEffect constructor pushes itself
    // into the module-global `activeEffectScope`, which during plugin setup is
    // Nuxt's per-request `nuxtApp._scope`. Nuxt never calls `_scope.stop()` on a
    // normal SSR response, so that one effect keeps this setup closure, and
    // through it the entire NuxtApp, reachable forever: one leaked render
    // context per request, which took production pods from a flat 285 MB to an
    // OOM at the 2 GB V8 heap cap. `1.auth.ts` guards its own watchers the same
    // way, which is why it never leaked.
    if (import.meta.client) {
      watch(
        () => guest.loaded.value,
        (loaded) => {
          if (!loaded || !loggedIn.value)
            return

          mergeGuestWishlist().catch((error) => {
            console.error(
              'Failed to merge the guest wishlist into the account',
              error,
            )
          })
        },
      )

      // Signing out leaves this browser to whoever uses it next, so the saves
      // still held in it go too — a merge that failed, or a heart from a tab
      // that had not noticed the sign-in, would otherwise show on the next
      // person's list and merge into their account. Only on an explicit sign-out:
      // `loggedIn` also turns false when a customer refresh merely fails, and
      // those saves are the signed-in customer's own. Clearing writes storage,
      // so other open tabs follow.
      //
      // Saves still held here belong to the customer signing out, and the hook
      // runs while the session holds: they go to that account first, then are
      // cleared — but sign-out never waits long on a bad connection for it. A
      // merge step still queued when the wait ends finds its entry cleared and
      // sends nothing; a reply already on its way is dropped with the session.
      _nuxtApp.hook('auth:logout', async () => {
        if (loggedIn.value && (guest.entries.value.length || guestLooks.entries.value.length)) {
          const saved = (async () => {
            await mergeGuestWishlist().catch(error => console.error('Failed to save the guest wishlist before sign-out', error))
            await mergeGuestSavedLooks().catch(error => console.error('Failed to save the guest saved outfits before sign-out', error))
          })()
          await Promise.race([saved, new Promise(resolve => setTimeout(resolve, 4000))])
        }
        guest.clear()
        guestLooks.clear()
      })

      // The outfits' list is read by its own client plugin, so it has its own
      // starting gun — the same reasoning, the same guard.
      watch(
        () => guestLooks.loaded.value,
        (loaded) => {
          if (!loaded || !loggedIn.value)
            return

          mergeGuestSavedLooks().catch((error) => {
            console.error('Failed to merge the guest saved outfits into the account', error)
          })
        },
      )
    }

    // Whose list `state` holds. A sign-in straight over another (a reset or
    // verification link, the login form while signed in) never passes through
    // signed-out, so the list is emptied here when the customer differs rather
    // than showing the previous customer's saves while the new ones load.
    let listOwner: string | null = null
    // When the account's list was last read whole (ms), 0 before the first read and after a sign-out
    // or a change of customer: lets a page that wants a fresh list skip asking again seconds after
    // this plugin did (`/account/wishlist` on a hard load).
    const readAt = ref(0)

    const { refresh: refreshWishlist } = await useAsyncData(
      'wishlist',
      async () => {
        if (!loggedIn?.value) {
          // Cleared on sign-out too, so the next person on this device never
          // inherits the previous customer's hearts (the browser's own lists
          // are cleared by the `auth:logout` hook above).
          state.value = []
          savedLookIds.value = []
          listOwner = null
          readAt.value = 0
          return null
        }

        // From here every reply written is this customer's: `serially` drops
        // any sent in another's session.
        const owner = String(customerId.value)
        if (listOwner !== owner) {
          state.value = []
          savedLookIds.value = []
          listOwner = owner
          readAt.value = 0
        }

        // Held across the merges too, so nothing reads the list as settled between their requests.
        inFlight.value++
        try {
          await serially(() => useGraphqlQuery('activeCustomerWishlist'), ({ data, errors }) => {
            // A read that failed is not an empty list: what is shown stays, as `wishlistReply` keeps
            // it for the mutations, and the list does not count as freshly read.
            if (!data?.activeCustomer && errors?.length)
              return
            state.value = (data?.activeCustomer?.wishlistItems
              ?? []) as WishlistItemFragment[]
            savedLookIds.value = toLookIds(data?.activeCustomer?.savedLooks)
            readAt.value = Date.now()
          })

          // Sequenced inside this handler rather than in a second watcher, so the
          // merge can never race the assignment above and re-add what it just saw.
          await mergeGuestWishlist().catch((error) => {
            console.error(
              'Failed to merge the guest wishlist into the account',
              error,
            )
          })
          await mergeGuestSavedLooks().catch((error) => {
            console.error('Failed to merge the guest saved outfits into the account', error)
          })
        }
        finally {
          inFlight.value--
        }

        return state.value
      },
      {
        lazy: true,
        deep: false,
        immediate: !state.value.length,
        // The id, not `loggedIn`: it also changes from one customer to another.
        watch: [customerId],
        // Always asked, never answered from the page's payload, as `activeCustomer` is
        // (`layers/common/app/plugins/1.auth.ts`). The server reads as a guest and ships `null`;
        // on an account page the customer arrives while the app is still hydrating, and the
        // default then answered every read — this watch's, the wishlist page's refresh — with that
        // `null`, so a hard load of any account page showed an empty wishlist.
        getCachedData: () => undefined,
      },
    )

    // Both mutations return the full, updated wishlist, so we sync state directly
    // from the response instead of refetching.
    const addToWishlist = async (
      productVariantId: string,
      productId?: string,
    ) => {
      if (!loggedIn.value) {
        if (!productId) {
          // Without it the row could never be hydrated on the wishlist page, and
          // a heart that fills but saves nothing is worse than one that does not.
          console.warn(
            'Guest wishlist save skipped: no productId for variant',
            productVariantId,
          )
          return
        }

        guest.add({ variantId: productVariantId, productId })
        return
      }

      await serially(() => useGraphqlMutation('addToWishlist', { productVariantId }), ({ data, errors }) => {
        state.value = wishlistReply(data?.addToWishlist, errors, 'addToWishlist')
      })
    }

    const removeFromWishlist = async (productVariantId: string) => {
      // Always drop the local copy, signed in or not. `wishlistedVariantIds` is
      // the union of both lists, so leaving a stale local entry behind would put
      // the heart straight back on. A merge still in flight skips the id once it
      // is gone from here, and an add of it already sent runs before this
      // removal (`serially`), so neither can put it back on the account.
      guest.remove(productVariantId)

      if (!loggedIn.value)
        return

      await serially(() => useGraphqlMutation('removeFromWishlist', { productVariantId }), ({ data, errors }) => {
        state.value = wishlistReply(data?.removeFromWishlist, errors, 'removeFromWishlist')
      })
    }

    /** Every saved variant of each product, the account's and the browser's, newest account saves first. */
    const savedVariantsByProduct = computed(() => {
      const byProduct = new Map<string, string[]>()
      const add = (productId: string, variantId: string) => {
        const variants = byProduct.get(productId) ?? []
        if (!variants.includes(variantId))
          byProduct.set(productId, [...variants, variantId])
      }
      for (const item of state.value)
        add(String(item.productVariant.product.id), String(item.productVariant.id))
      for (const entry of guest.entries.value)
        add(entry.productId, entry.variantId)
      return byProduct
    })

    const isProductWishlisted = (productId: string | number) => savedVariantsByProduct.value.has(String(productId))

    // A heart that stands for the product (a tile's, the product page's) takes
    // off whichever sizes of it are saved, not just the one it would have saved.
    // All are queued at once (`serially` keeps them in order): one that fails does
    // not stop the rest, and the caller hears of it once, after they all settled.
    const removeProductFromWishlist = async (productId: string | number) => {
      const variantIds = [...(savedVariantsByProduct.value.get(String(productId)) ?? [])]
      const results = await Promise.allSettled(variantIds.map(variantId => removeFromWishlist(variantId)))
      const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
      if (failed)
        throw failed.reason
    }

    const toggleWishlist = async (
      productVariantId: string,
      isObserved: boolean,
      productId?: string,
    ) => {
      if (isObserved)
        await removeFromWishlist(productVariantId)
      else await addToWishlist(productVariantId, productId)
    }

    // The outfit heart. Same shape as the product one: a guest's save lands in
    // the browser, a customer's on the account, and the reply is the full list.
    const saveLook = async (lookId: string) => {
      if (!loggedIn.value) {
        guestLooks.add(lookId)
        return
      }

      await serially(() => useGraphqlMutation('addLookToWishlist', { lookId }), ({ data, errors }) => {
        savedLookIds.value = toLookIds(wishlistReply(data?.addLookToWishlist, errors, 'addLookToWishlist'))
      })
    }

    const unsaveLook = async (lookId: string) => {
      // Always drop the local copy — `savedLookIdSet` is the union, see
      // `removeFromWishlist` for why leaving it behind would undo the removal.
      guestLooks.remove(lookId)

      if (!loggedIn.value)
        return

      await serially(() => useGraphqlMutation('removeLookFromWishlist', { lookId }), ({ data, errors }) => {
        savedLookIds.value = toLookIds(wishlistReply(data?.removeLookFromWishlist, errors, 'removeLookFromWishlist'))
      })
    }

    const savedLookIdSet = computed(() => {
      const ids = new Set(savedLookIds.value.map(String))
      for (const id of guestLooks.entries.value) ids.add(id)
      return ids
    })

    const isLookSaved = (lookId: string | number) => savedLookIdSet.value.has(String(lookId))

    const toggleLook = async (lookId: string | number) => {
      const id = String(lookId)
      if (isLookSaved(id))
        await unsaveLook(id)
      else await saveLook(id)
    }

    // The union of both lists: a guest's saves and, once signed in, the account's.
    // After a merge the local list is empty, so this is simply the account list.
    const wishlistedVariantIds = computed(() => {
      const ids = new Set(
        state.value.map(item => String(item.productVariant.id)),
      )
      for (const entry of guest.entries.value) ids.add(entry.variantId)
      return ids
    })

    const isProductVariantObserved = (productVariantId: string) =>
      wishlistedVariantIds.value.has(String(productVariantId))

    // Everything on the wishlist page: products and outfits alike.
    const wishlistTotalQuantity = computed(
      () => wishlistedVariantIds.value.size + savedLookIdSet.value.size,
    )

    const clearWishlist = async () => {
      state.value = []
      savedLookIds.value = []
    }

    return {
      provide: {
        observed: {
          items: readonly(state),
          guestEntries: readonly(guest.entries),
          guestWishlistLoaded: readonly(guest.loaded),
          wishlistedVariantIds,
          wishlistTotalQuantity,
          loading,
          readAt: readonly(readAt),
          isWishlisted: isProductVariantObserved,
          isProductVariantObserved,
          isProductWishlisted,
          removeProductFromWishlist,
          addToObserved: addToWishlist,
          addToWishlist,
          removeFromObserved: removeFromWishlist,
          removeFromWishlist,
          toggleObserved: toggleWishlist,
          toggleWishlist,
          clearWishlist,
          refreshWishlist,
          loadGuestWishlist: guest.load,
          keepGuestWishlistEntries: guest.keepOnly,
          savedLookIds: readonly(savedLookIds),
          guestLookIds: readonly(guestLooks.entries),
          guestSavedLooksLoaded: readonly(guestLooks.loaded),
          savedLookIdSet,
          isLookSaved,
          saveLook,
          unsaveLook,
          toggleLook,
          keepGuestSavedLooks: guestLooks.keepOnly,
        },
      },
    }
  },
})

interface ObservedPlugin {
  items: Readonly<Ref<WishlistItemFragment[]>>
  /** Ids a logged-out visitor has saved locally; empty once merged into an account. */
  guestEntries: Readonly<Ref<{ variantId: string, productId: string }[]>>
  /** False until the browser-held list has been read, which happens after mount. */
  guestWishlistLoaded: Readonly<Ref<boolean>>
  wishlistedVariantIds: ComputedRef<Set<string>>
  wishlistTotalQuantity: ComputedRef<number>
  loading: Readonly<Ref<boolean>>
  /** When the account's list was last read whole (ms since epoch); 0 before the first read. */
  readAt: Readonly<Ref<number>>
  /** Keyed by product variant id. */
  isWishlisted: (productVariantId: string) => boolean
  isProductVariantObserved: (productVariantId: string) => boolean
  /** Whether any size of the product is saved. */
  isProductWishlisted: (productId: string | number) => boolean
  /** Removes every saved size of the product, one after another. */
  removeProductFromWishlist: (productId: string | number) => Promise<void>
  addToObserved: (
    productVariantId: string,
    productId?: string,
  ) => Promise<void>
  addToWishlist: (
    productVariantId: string,
    productId?: string,
  ) => Promise<void>
  removeFromObserved: (productVariantId: string) => Promise<void>
  removeFromWishlist: (productVariantId: string) => Promise<void>
  toggleObserved: (
    productVariantId: string,
    isObserved: boolean,
    productId?: string,
  ) => Promise<void>
  toggleWishlist: (
    productVariantId: string,
    isObserved: boolean,
    productId?: string,
  ) => Promise<void>
  clearWishlist: () => Promise<void>
  refreshWishlist: () => Promise<void>
  loadGuestWishlist: () => void
  /** Prunes locally-held ids the catalogue no longer resolves. */
  keepGuestWishlistEntries: (variantIds: Iterable<string>) => void
  /** The account's saved outfits, newest first; empty for a guest. */
  savedLookIds: Readonly<Ref<string[]>>
  /** Outfit ids a logged-out visitor saved locally; empty once merged into an account. */
  guestLookIds: Readonly<Ref<string[]>>
  /** False until the browser-held outfit list has been read, which happens after mount. */
  guestSavedLooksLoaded: Readonly<Ref<boolean>>
  /** The union of both outfit lists. */
  savedLookIdSet: ComputedRef<Set<string>>
  isLookSaved: (lookId: string | number) => boolean
  saveLook: (lookId: string) => Promise<void>
  unsaveLook: (lookId: string) => Promise<void>
  toggleLook: (lookId: string | number) => Promise<void>
  /** Prunes locally-held outfit ids the catalogue no longer resolves. */
  keepGuestSavedLooks: (lookIds: Iterable<string>) => void
}

declare module '#app' {
  interface NuxtApp {
    $observed: ObservedPlugin
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $observed: ObservedPlugin
  }
}
